import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SITE_CONFIG, NAVER_BLOG_URL } from '../data/siteConfig';
import { Icon } from './icons';
import OfficialPriceModal from './OfficialPriceModal';
import { useYoutubeChannel } from '../utils/useYoutubeChannel';
import useIsMobile from '../utils/useIsMobile';

export default function Footer() {
  const [officialPriceOpen, setOfficialPriceOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { url: ytUrl, enabled: ytEnabled } = useYoutubeChannel();
  const isMobile = useIsMobile();

  // On mobile the 3 columns collapse to 1; center everything instead of the
  // default left alignment. flexJustify centers icon+text rows (연락처).
  const colAlign = isMobile ? 'center' : 'left';
  const flexJustify = isMobile ? 'center' : 'flex-start';

  // NavBar와 동일 분기: 홈이면 #pricing 섹션 스크롤, 그 외엔 팝업
  const handlePricingClick = () => {
    if (location.pathname === '/') {
      navigate('/#pricing');
    } else {
      setOfficialPriceOpen(true);
    }
  };

  return (
    <footer style={{
      background: 'var(--surface)', borderTop: '1px solid var(--border)',
      padding: '48px 0 24px',
    }}>
      <div className="container" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 32,
      }}>
        {/* Column 1: Brand */}
        <div style={{ textAlign: colAlign }}>
          <h3 style={{ color: 'var(--primary)', marginBottom: 12 }}>
            {SITE_CONFIG.name}
          </h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.6 }}>
            {SITE_CONFIG.tagline}
            <br />
            {SITE_CONFIG.address}
          </p>
        </div>

        {/* Column 2: Links */}
        <div style={{ textAlign: colAlign }}>
          <h4 style={{ marginBottom: 12, color: 'var(--text)' }}>메뉴</h4>
          <button
            type="button"
            onClick={handlePricingClick}
            style={{
              display: 'block', width: '100%', color: 'var(--text-muted)',
              fontSize: '0.9rem', marginBottom: 8, padding: 0,
              background: 'none', border: 'none', cursor: 'pointer',
              textAlign: colAlign,
            }}
          >
            서비스&amp;가격
          </button>
          <Link to="/directions" style={{
            display: 'block', color: 'var(--text-muted)',
            fontSize: '0.9rem', marginBottom: 8,
          }}>
            오시는길
          </Link>
          <a
            href={NAVER_BLOG_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'block', color: 'var(--text-muted)',
              fontSize: '0.9rem', marginBottom: 8,
            }}
          >
            네이버 블로그
          </a>
          {ytEnabled && (
            <a
              href={ytUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'block', color: 'var(--text-muted)',
                fontSize: '0.9rem', marginBottom: 8,
              }}
            >
              유튜브 채널
            </a>
          )}
        </div>

        {/* Column 3: Contact */}
        <div style={{ textAlign: colAlign }}>
          <h4 style={{ marginBottom: 12, color: 'var(--text)' }}>연락처</h4>
          <a href={`tel:${SITE_CONFIG.phone}`} style={{
            display: 'flex', alignItems: 'center', justifyContent: flexJustify, gap: 8,
            color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: 8,
          }}>
            <Icon name="Phone" size={16} /> {SITE_CONFIG.phone}
          </a>
          <a href="tel:010-5735-5920" style={{
            display: 'flex', alignItems: 'center', justifyContent: flexJustify, gap: 8,
            color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: 8,
          }}>
            <Icon name="Phone" size={16} /> 010-5735-5920
          </a>
          <a href={`sms:${SITE_CONFIG.phone}`} style={{
            display: 'flex', alignItems: 'center', justifyContent: flexJustify, gap: 8,
            color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: 8,
          }}>
            <Icon name="MessageSquare" size={16} /> 문자 문의
          </a>
          <span style={{
            display: 'flex', alignItems: 'center', justifyContent: flexJustify, gap: 8,
            color: 'var(--text-muted)', fontSize: '0.9rem',
          }}>
            <Icon name="MessageCircle" size={16} /> 카카오톡 상담
          </span>
        </div>
      </div>

      {/* Business info — 전자상거래법 법적 공개 */}
      <div className="container" style={{
        borderTop: '1px solid var(--border)', marginTop: 32,
        paddingTop: 20, fontSize: '0.78rem',
        color: 'var(--text-dim)', lineHeight: 1.8,
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: flexJustify, gap: '6px 16px' }}>
          <span>상호: {SITE_CONFIG.business.companyName}</span>
          <span>대표자: {SITE_CONFIG.business.representative}</span>
          <span>
            사업자등록번호: {SITE_CONFIG.business.registrationNumber || '준비 중'}
          </span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: flexJustify, gap: '6px 16px', marginTop: 4 }}>
          <span>주소: {SITE_CONFIG.business.businessAddress}</span>
          <span>전화: {SITE_CONFIG.phone}</span>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="container" style={{
        borderTop: '1px solid var(--border)', marginTop: 16,
        paddingTop: 16, display: 'flex',
        justifyContent: isMobile ? 'center' : 'space-between',
        textAlign: isMobile ? 'center' : 'left',
        flexWrap: 'wrap', gap: 12,
        fontSize: '0.85rem', color: 'var(--text-dim)',
      }}>
        <span>
          영업시간: {SITE_CONFIG.hours.summary}
        </span>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Link to="/terms" style={{ color: 'var(--text-muted)' }}>이용약관</Link>
          <span>|</span>
          <Link to="/privacy" style={{ color: 'var(--text-muted)' }}>개인정보처리방침</Link>
        </span>
        <span>© 2026 {SITE_CONFIG.business.companyName}. All rights reserved.</span>
      </div>

      <OfficialPriceModal
        open={officialPriceOpen}
        onClose={() => setOfficialPriceOpen(false)}
      />
    </footer>
  );
}
